import { useCallback, useRef, useEffect } from 'react';

/**
 * Audio context singleton
 */
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
}

/**
 * Convert MIDI number to frequency (Hz)
 * A4 = 440Hz = MIDI 69
 */
function midiToFrequency(midiNumber: number): number {
  return 440 * Math.pow(2, (midiNumber - 69) / 12);
}

interface UseAudioOptions {
  /** Whether audio is enabled */
  enabled: boolean;
  /** Volume level (0-1) */
  volume?: number;
}

interface UseAudioReturn {
  /** Play a note by MIDI number */
  playNote: (midiNumber: number, duration?: number) => void;
  /** Stop all currently playing notes */
  stopAll: () => void;
  /** Resume audio context (needed after user interaction) */
  resumeAudio: () => Promise<void>;
}

/**
 * Hook for playing audio notes
 *
 * Uses Web Audio API to generate piano-like tones.
 * This is a simple synthesizer - for production, you'd want to use
 * sampled piano sounds.
 */
export function useAudio({ enabled, volume = 0.3 }: UseAudioOptions): UseAudioReturn {
  const activeOscillators = useRef<Set<OscillatorNode>>(new Set());

  // Clean up on unmount
  useEffect(() => {
    return () => {
      activeOscillators.current.forEach(osc => {
        try {
          osc.stop();
        } catch {
          // Ignore errors if already stopped
        }
      });
    };
  }, []);

  const playNote = useCallback((midiNumber: number, duration: number = 0.5) => {
    if (!enabled) return;

    try {
      const ctx = getAudioContext();

      // Resume context if suspended (required by browsers)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const frequency = midiToFrequency(midiNumber);

      // Create oscillator for the fundamental
      const oscillator = ctx.createOscillator();
      oscillator.type = 'triangle'; // Softer than sine, more piano-like

      // Create gain node for envelope
      const gainNode = ctx.createGain();

      // Connect the chain
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Set frequency
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

      // Create envelope (attack-decay-sustain-release)
      const now = ctx.currentTime;
      const attackTime = 0.02;
      const decayTime = 0.1;
      const sustainLevel = volume * 0.7;
      const releaseTime = 0.3;

      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + attackTime);
      gainNode.gain.linearRampToValueAtTime(sustainLevel, now + attackTime + decayTime);
      gainNode.gain.setValueAtTime(sustainLevel, now + duration - releaseTime);
      gainNode.gain.linearRampToValueAtTime(0, now + duration);

      // Start and stop
      oscillator.start(now);
      oscillator.stop(now + duration);

      // Track active oscillators
      activeOscillators.current.add(oscillator);
      oscillator.onended = () => {
        activeOscillators.current.delete(oscillator);
      };
    } catch (error) {
      console.warn('Error playing audio:', error);
    }
  }, [enabled, volume]);

  const stopAll = useCallback(() => {
    activeOscillators.current.forEach(osc => {
      try {
        osc.stop();
      } catch {
        // Ignore errors if already stopped
      }
    });
    activeOscillators.current.clear();
  }, []);

  const resumeAudio = useCallback(async () => {
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
    } catch (error) {
      console.warn('Error resuming audio:', error);
    }
  }, []);

  return { playNote, stopAll, resumeAudio };
}

/**
 * Stub function for future integration with sampled sounds
 *
 * To integrate real piano samples:
 * 1. Load audio samples for each note (or use a soundfont)
 * 2. Replace playNote with sample playback
 * 3. Consider using a library like Tone.js or soundfont-player
 */
export async function loadPianoSamples(): Promise<void> {
  // Stub - implement when integrating real samples
  console.log('Piano samples loading not yet implemented');
}
